import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, LANG_LABELS } from '@/i18n';
import { TECH_SYSTEMS, systemLabel } from '@/lib/systems';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Plus, Trash2, X, Edit3, Search, Sparkles,
  CheckCircle2, AlertTriangle, ChevronRight, ChevronDown, Wrench,
  ArrowUp, ArrowDown, Target, Languages, BadgeCheck, Wand2, ShieldCheck,
} from 'lucide-react';

const _uid = () => `_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const emptyLoc = () => ({ it: '', es: '', en: '' });
const emptyStep = (n = 1) => ({ _uid: _uid(), order: n, it: '', es: '', en: '', expected_value: '' });
const emptyEv = () => ({ _uid: _uid(), test: '', value: '', unit: '', it: '', es: '', en: '' });

const EMPTY = {
  brand: '', model: '', error_code: '',
  system: 'electronics',
  symptom: emptyLoc(),
  probable_cause: emptyLoc(),
  notes: emptyLoc(),
  procedure_steps: [emptyStep(1)],
  expected_values: [],
  confidence: 3,
  success_count: 0,
  translation_status: { it: 'draft', es: 'draft', en: 'draft' },
};

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  auto_translated: 'bg-amber-100 text-amber-800 border-amber-300',
  validated: 'bg-green-100 text-green-800 border-green-400',
};

function pickLang(field, lang) {
  if (!field || typeof field !== 'object') return field || '';
  return field[lang] || field.it || field.en || field.es || '';
}

export default function ProcedurePatternsPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [activeLang, setActiveLang] = useState(lang);
  const [search, setSearch] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [translating, setTranslating] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (search.trim()) params.q = search.trim();
    if (systemFilter) params.system = systemFilter;
    api.get('/procedure-patterns', { params }).then((r) => setItems(r.data));
  }, [search, systemFilter]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setActiveLang(lang); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    const sysVal = typeof p.system === 'string' ? p.system : (p.system?.it || 'electronics');
    setForm({
      ...EMPTY,
      ...p,
      system: TECH_SYSTEMS.includes(sysVal) ? sysVal : 'electronics',
      symptom: { ...emptyLoc(), ...(p.symptom || {}) },
      probable_cause: { ...emptyLoc(), ...(p.probable_cause || {}) },
      notes: { ...emptyLoc(), ...(p.notes || {}) },
      procedure_steps: (p.procedure_steps || []).length ? p.procedure_steps.map((s) => ({ ...emptyStep(), ...s, _uid: s._uid || _uid() })) : [emptyStep(1)],
      expected_values: (p.expected_values || []).map((e) => ({ ...emptyEv(), ...e, _uid: e._uid || _uid() })),
      translation_status: p.translation_status || { it: 'draft', es: 'draft', en: 'draft' },
    });
    setActiveLang(lang);
    setOpen(true);
  };

  const setLocField = (key, l, val) => setForm((f) => ({ ...f, [key]: { ...(f[key] || {}), [l]: val } }));
  const setStep = (i, key, val) => setForm((f) => ({
    ...f, procedure_steps: f.procedure_steps.map((s, idx) => idx === i ? { ...s, [key]: val } : s),
  }));
  const setStepLoc = (i, l, val) => setStep(i, l, val);
  const addStep = () => setForm((f) => ({
    ...f,
    procedure_steps: [...f.procedure_steps, emptyStep(f.procedure_steps.length + 1)],
  }));
  const removeStep = (i) => setForm((f) => ({
    ...f,
    procedure_steps: f.procedure_steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })),
  }));
  const moveStep = (i, dir) => setForm((f) => {
    const arr = [...f.procedure_steps];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, procedure_steps: arr.map((s, idx) => ({ ...s, order: idx + 1 })) };
  });

  const setEv = (i, key, val) => setForm((f) => ({
    ...f, expected_values: f.expected_values.map((e, idx) => idx === i ? { ...e, [key]: val } : e),
  }));
  const addEv = () => setForm((f) => ({ ...f, expected_values: [...f.expected_values, emptyEv()] }));
  const removeEv = (i) => setForm((f) => ({ ...f, expected_values: f.expected_values.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      confidence: Number(form.confidence),
      success_count: Number(form.success_count),
      procedure_steps: form.procedure_steps
        .filter((s) => (s.it || s.es || s.en || '').trim())
        .map((s, idx) => {
          const { _uid: _ignored, ...rest } = s;
          return { ...rest, order: idx + 1 };
        }),
      expected_values: form.expected_values
        .filter((e) => (e.test || '').trim())
        .map(({ _uid: _ignored, ...rest }) => rest),
    };
    try {
      let saved;
      if (editing) {
        saved = (await api.put(`/procedure-patterns/${editing.id}`, payload)).data;
        toast.success(t('app.update'));
      } else {
        saved = (await api.post('/procedure-patterns', payload)).data;
        toast.success(t('app.create'));
      }
      setEditing(saved); // Allow continuing to translate
      const sysVal = typeof saved.system === 'string' ? saved.system : 'electronics';
      setForm({
        ...EMPTY,
        ...saved,
        system: TECH_SYSTEMS.includes(sysVal) ? sysVal : 'electronics',
        symptom: { ...emptyLoc(), ...(saved.symptom || {}) },
        probable_cause: { ...emptyLoc(), ...(saved.probable_cause || {}) },
        notes: { ...emptyLoc(), ...(saved.notes || {}) },
        procedure_steps: (saved.procedure_steps || []).length ? saved.procedure_steps.map((s) => ({ ...emptyStep(), ...s, _uid: s._uid || _uid() })) : [emptyStep(1)],
        expected_values: (saved.expected_values || []).map((e) => ({ ...emptyEv(), ...e, _uid: e._uid || _uid() })),
        translation_status: saved.translation_status || { it: 'draft', es: 'draft', en: 'draft' },
      });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const incrementSuccess = async (p) => {
    try {
      await api.post(`/procedure-patterns/${p.id}/success`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const validateTranslation = async (l) => {
    if (!editing) return;
    try {
      const { data } = await api.post(`/procedure-patterns/${editing.id}/validate-translation`, { language: l });
      setEditing(data);
      setForm((f) => ({ ...f, translation_status: data.translation_status }));
      toast.success(`${t('patterns.validated_translation')}: ${LANG_LABELS[l].native}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const generateMissing = async () => {
    if (!editing) {
      toast.error(t('patterns.save_first_to_translate'));
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post(`/procedure-patterns/${editing.id}/auto-translate`, {});
      setEditing(data);
      const sysVal = typeof data.system === 'string' ? data.system : 'electronics';
      setForm({
        ...EMPTY,
        ...data,
        system: TECH_SYSTEMS.includes(sysVal) ? sysVal : 'electronics',
        symptom: { ...emptyLoc(), ...(data.symptom || {}) },
        probable_cause: { ...emptyLoc(), ...(data.probable_cause || {}) },
        notes: { ...emptyLoc(), ...(data.notes || {}) },
        procedure_steps: (data.procedure_steps || []).length ? data.procedure_steps.map((s) => ({ ...emptyStep(), ...s, _uid: s._uid || _uid() })) : [emptyStep(1)],
        expected_values: (data.expected_values || []).map((e) => ({ ...emptyEv(), ...e, _uid: e._uid || _uid() })),
        translation_status: data.translation_status || { it: 'draft', es: 'draft', en: 'draft' },
      });
      toast.success(t('app.update'));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setTranslating(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/procedure-patterns/${confirmDelete.id}`);
      toast.success(t('app.delete'));
      setConfirmDelete(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setDeleting(false); }
  };

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Normalize brand for grouping (BOSCHUNG / Boschung / boshung → same key).
  const normKey = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  // Pick the most "canonical-looking" display name from observed variants
  // (most frequent, prefer Mixed Case over ALL CAPS).
  const pickCanonicalDisplay = (variants) => {
    const counts = {};
    for (const v of variants) counts[v] = (counts[v] || 0) + 1;
    const sorted = Object.entries(counts).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      const isMixed = (s) => /[A-Z]/.test(s) && /[a-z]/.test(s);
      if (isMixed(b[0]) !== isMixed(a[0])) return isMixed(b[0]) ? 1 : -1;
      return a[0].localeCompare(b[0]);
    });
    return sorted[0]?.[0] || '—';
  };

  // Brand → Model → Patterns tree. ALL nodes collapsed by default.
  const tree = useMemo(() => {
    const brands = new Map();
    for (const p of items) {
      const brandRaw = p.brand || '—';
      const bk = normKey(brandRaw) || '__unknown__';
      const b = brands.get(bk) || { key: bk, brandVariants: [], models: new Map() };
      b.brandVariants.push(brandRaw);

      const modelRaw = p.model || '—';
      const mk = normKey(modelRaw) || '__nomodel__';
      const m = b.models.get(mk) || { key: mk, modelVariants: [], items: [] };
      m.modelVariants.push(modelRaw);
      m.items.push(p);

      b.models.set(mk, m);
      brands.set(bk, b);
    }
    return Array.from(brands.values()).map((b) => ({
      ...b,
      brandDisplay: pickCanonicalDisplay(b.brandVariants),
      models: Array.from(b.models.values()).map((m) => ({
        ...m,
        modelDisplay: pickCanonicalDisplay(m.modelVariants),
        items: m.items.slice().sort((a, b2) => {
          // Validated patterns first, then by success_count desc, then by error_code
          const va = a.validated_by_name ? 1 : 0;
          const vb = b2.validated_by_name ? 1 : 0;
          if (va !== vb) return vb - va;
          if ((a.success_count || 0) !== (b2.success_count || 0)) return (b2.success_count || 0) - (a.success_count || 0);
          return (a.error_code || '').localeCompare(b2.error_code || '');
        }),
      })).sort((a, b2) => a.modelDisplay.localeCompare(b2.modelDisplay, undefined, { sensitivity: 'base' })),
    })).sort((a, b2) => a.brandDisplay.localeCompare(b2.brandDisplay, undefined, { sensitivity: 'base' }));
  }, [items]);

  const expandAll = () => {
    const next = {};
    tree.forEach((b) => {
      next[`b:${b.key}`] = true;
      b.models.forEach((m) => { next[`m:${b.key}:${m.key}`] = true; });
    });
    setExpanded(next);
  };
  const collapseAll = () => setExpanded({});

  return (
    <div className="animate-fade-up" data-testid="patterns-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#002FA7]">PROCEDURE PATTERNS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">
            <span className="inline-flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-[#002FA7]" strokeWidth={1.5} /> {t('patterns.title')}
            </span>
          </h1>
          <p className="text-gray-500 mt-2 max-w-2xl">{t('patterns.subtitle')}</p>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="new-pattern-btn">
          <Plus className="w-4 h-4" /> {t('patterns.new')}
        </button>
      </div>

      {/* Search + filter */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <div className="sm:col-span-2 relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('app.search')}
            className="input-base pl-10" data-testid="pattern-search"
          />
        </div>
        <select value={systemFilter} onChange={(e) => setSystemFilter(e.target.value)} className="input-base" data-testid="pattern-system-filter">
          <option value="">{t('app.all')}</option>
          {TECH_SYSTEMS.map((s) => <option key={s} value={s}>{systemLabel(s, lang)}</option>)}
        </select>
      </div>

      <div className="mt-6 space-y-3" data-testid="patterns-list">
        {items.length === 0 ? (
          <div className="surface p-12 text-center">
            <Sparkles className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="mt-3 font-display font-bold text-lg">{t('patterns.no_patterns')}</div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 justify-end -mt-1 -mb-1" data-testid="patterns-tree-controls">
              <button type="button" onClick={expandAll} className="text-xs text-gray-600 hover:text-[#002FA7] underline-offset-2 hover:underline" data-testid="patterns-expand-all">
                {t('documents_tree.expand_all')}
              </button>
              <span className="text-gray-300">·</span>
              <button type="button" onClick={collapseAll} className="text-xs text-gray-600 hover:text-[#002FA7] underline-offset-2 hover:underline" data-testid="patterns-collapse-all">
                {t('documents_tree.collapse_all')}
              </button>
            </div>
            {tree.map((b) => {
              const bId = `b:${b.key}`;
              const bOpen = !!expanded[bId];
              const totalPatterns = b.models.reduce((acc, m) => acc + m.items.length, 0);
              return (
                <div key={b.key} className="surface overflow-hidden" data-testid={`pattern-brand-${b.key}`}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(bId)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 text-left"
                    data-testid={`pattern-brand-header-${b.key}`}
                  >
                    {bOpen ? <ChevronDown className="w-4 h-4 text-[#002FA7]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <Wrench className="w-4 h-4 text-[#002FA7]" />
                    <span className="font-display font-bold uppercase tracking-wide">{b.brandDisplay}</span>
                    <span className="ml-auto text-xs text-gray-500 font-mono">{totalPatterns}</span>
                  </button>
                  {bOpen && (
                    <div className="divide-y divide-gray-200">
                      {b.models.map((m) => {
                        const mId = `m:${b.key}:${m.key}`;
                        const mOpen = !!expanded[mId];
                        return (
                          <div key={m.key} data-testid={`pattern-model-${b.key}-${m.key}`}>
                            <button
                              type="button"
                              onClick={() => toggleExpand(mId)}
                              className="w-full flex items-center gap-3 px-6 py-2.5 bg-white hover:bg-gray-50 text-left"
                              data-testid={`pattern-model-header-${b.key}-${m.key}`}
                            >
                              {mOpen ? <ChevronDown className="w-4 h-4 text-[#002FA7]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                              <span className="font-mono text-sm font-semibold text-gray-800">{m.modelDisplay}</span>
                              <span className="ml-auto text-xs text-gray-500 font-mono">{m.items.length}</span>
                            </button>
                            {mOpen && (
                              <div className="divide-y divide-gray-200 bg-gray-50/50">
                                {m.items.map((p) => {
                                  const isOpen = !!expanded[p.id];
                                  const sysEnum = typeof p.system === 'string' ? p.system : 'electronics';
                                  const sysLbl = systemLabel(sysEnum, lang);
                                  const symLbl = pickLang(p.symptom, lang);
                                  const causeLbl = pickLang(p.probable_cause, lang);
                                  const status = p.translation_status || {};
                                  return (
                                    <div key={p.id} data-testid={`pattern-${p.id}`}>
                                      <button onClick={() => toggleExpand(p.id)} className="w-full flex items-start gap-3 p-3 pl-10 hover:bg-white text-left">
                                        {isOpen ? <ChevronDown className="w-4 h-4 text-[#002FA7] mt-1 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 mt-1 shrink-0" />}
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center flex-wrap gap-2">
                                            <span className="overline text-[#002FA7] px-2 py-0.5 bg-[#002FA7]/10 uppercase">{sysLbl}</span>
                                            {p.error_code && <span className="bg-red-100 text-red-800 px-2 py-0.5 text-xs font-mono font-bold">{p.error_code}</span>}
                                            <span className="text-xs text-gray-500 font-mono">conf {p.confidence}/5</span>
                                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 text-xs font-mono">
                                              <Target className="w-3 h-3" /> {p.success_count}
                                            </span>
                                            {p.validated_by_name && (
                                              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-mono">
                                                <BadgeCheck className="w-3 h-3" /> {t('patterns.validated_field')}
                                              </span>
                                            )}
                                            <span className="inline-flex items-center gap-1 ml-2">
                                              {SUPPORTED_LANGS.map((l) => (
                                                <span key={l} className={`px-1.5 py-0.5 text-[10px] uppercase font-mono border ${STATUS_BADGE[status[l]] || STATUS_BADGE.draft}`} title={status[l]}>
                                                  {l}
                                                </span>
                                              ))}
                                            </span>
                                          </div>
                                          <div className="font-display font-bold mt-1 text-sm sm:text-base">{symLbl || <em className="text-gray-400">{t('patterns.untranslated')}</em>}</div>
                                          {causeLbl && <div className="text-xs text-gray-500 mt-0.5 truncate">→ {causeLbl}</div>}
                                        </div>
                                      </button>
                                      {isOpen && (
                                        <div className="bg-white px-4 pb-4 pt-1 ml-6">
                                          <div className="flex gap-2 justify-end mb-3 flex-wrap">
                                            <button onClick={() => incrementSuccess(p)} className="inline-flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-green-700 active:scale-95" data-testid={`success-${p.id}`}>
                                              <CheckCircle2 className="w-3.5 h-3.5" /> {t('patterns.increment_success')}
                                            </button>
                                            <button onClick={() => startEdit(p)} className="btn-ghost text-xs" data-testid={`edit-${p.id}`}>
                                              <Edit3 className="w-3.5 h-3.5" /> {t('app.edit_action')}
                                            </button>
                                            {(user?.role === 'admin' || user?.id === p.created_by) && (
                                              <button onClick={() => setConfirmDelete(p)} className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-red-700 active:scale-95" data-testid={`del-${p.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" /> {t('app.delete')}
                                              </button>
                                            )}
                                          </div>
                                          <div className="bg-white border border-gray-200 p-3">
                                            <div className="overline text-gray-500">{t('patterns.procedure_steps')}</div>
                                            <ol className="mt-2 space-y-2">
                                              {(p.procedure_steps || []).map((s) => {
                                                const action = pickLang(s, lang);
                                                return (
                                                  <li key={s.order} className="flex items-start gap-3">
                                                    <span className="w-6 h-6 bg-[#002FA7] text-white flex items-center justify-center text-xs font-mono font-bold shrink-0">{s.order}</span>
                                                    <div className="flex-1 text-sm">
                                                      <div className="font-semibold text-gray-900">{action || <em className="text-gray-400">{t('patterns.untranslated')}</em>}</div>
                                                      {s.expected_value && (
                                                        <div className="text-xs text-gray-600 font-mono mt-0.5">
                                                          <span className="overline text-green-700">{t('patterns.step_expected')}:</span> {s.expected_value}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </li>
                                                );
                                              })}
                                            </ol>
                                            {pickLang(p.notes, lang) && (
                                              <div className="mt-3 pt-3 border-t border-gray-100">
                                                <div className="overline text-gray-500 mb-1">{t('patterns.notes')}</div>
                                                <div className="text-sm text-gray-700 whitespace-pre-wrap">{pickLang(p.notes, lang)}</div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Form Modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white w-full sm:max-w-3xl border border-gray-200 max-h-[95vh] overflow-y-auto" data-testid="pattern-form">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="font-display font-bold text-lg">{editing ? t('app.edit_action') : t('patterns.new')}</div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Shared fields (no translation) */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><label className="overline text-gray-600 block mb-1">{t('patterns.brand')} *</label>
                  <input required className="input-base" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="p-brand" /></div>
                <div><label className="overline text-gray-600 block mb-1">{t('patterns.model')} *</label>
                  <input required className="input-base" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="p-model" /></div>
                <div><label className="overline text-gray-600 block mb-1">{t('patterns.error_code')}</label>
                  <input className="input-base font-mono" value={form.error_code} onChange={(e) => setForm({ ...form, error_code: e.target.value })} data-testid="p-code" /></div>
              </div>

              {/* Tech system dropdown - mandatory, single enum stored in English */}
              <div>
                <label className="overline text-gray-600 block mb-1">{t('patterns.system')} *</label>
                <select required className="input-base" value={form.system}
                  onChange={(e) => setForm({ ...form, system: e.target.value })}
                  data-testid="p-system">
                  {TECH_SYSTEMS.map((s) => (
                    <option key={s} value={s}>{systemLabel(s, lang)}</option>
                  ))}
                </select>
              </div>

              {/* Translation status banner + actions */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 p-3 flex flex-wrap items-center gap-3">
                <Languages className="w-5 h-5 text-[#002FA7]" />
                <span className="overline text-gray-600">{t('patterns.translation_status')}:</span>
                {SUPPORTED_LANGS.map((l) => (
                  <span key={l} className={`px-2 py-1 text-[11px] uppercase font-mono border ${STATUS_BADGE[form.translation_status?.[l]] || STATUS_BADGE.draft}`}>
                    {l} · {t(`patterns.status_${form.translation_status?.[l] === 'auto_translated' ? 'auto' : (form.translation_status?.[l] || 'draft')}`)}
                  </span>
                ))}
                <div className="flex-1" />
                {user?.role === 'admin' && (
                  <button type="button" onClick={generateMissing} disabled={!editing || translating}
                    className="inline-flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-600 disabled:opacity-50"
                    data-testid="auto-translate-btn">
                    <Wand2 className="w-3.5 h-3.5" /> {translating ? t('patterns.translating') : t('patterns.auto_translate')}
                  </button>
                )}
              </div>

              {/* Language tabs */}
              <div className="border-b border-gray-200 flex">
                {SUPPORTED_LANGS.map((l) => (
                  <button key={l} type="button" onClick={() => setActiveLang(l)}
                    className={[
                      'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors',
                      activeLang === l ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-500 hover:text-gray-800',
                    ].join(' ')}
                    data-testid={`lang-tab-${l}`}>
                    <span className="mr-1">{LANG_LABELS[l].flag}</span>
                    {LANG_LABELS[l].native}
                    {form.translation_status?.[l] === 'auto_translated' && <span className="ml-2 text-amber-600 text-[10px]">(auto)</span>}
                    {form.translation_status?.[l] === 'validated' && <BadgeCheck className="w-3.5 h-3.5 ml-1 inline text-green-600" />}
                  </button>
                ))}
              </div>

              <div>
                <label className="overline text-gray-600 block mb-1">{t('patterns.symptom')} ({activeLang}) {activeLang === 'it' && '*'}</label>
                <textarea required={activeLang === 'it'} rows={2} className="input-base" value={form.symptom?.[activeLang] || ''}
                  onChange={(e) => setLocField('symptom', activeLang, e.target.value)}
                  data-testid={`p-symptom-${activeLang}`} />
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">{t('patterns.probable_cause')} ({activeLang})</label>
                <textarea rows={2} className="input-base" value={form.probable_cause?.[activeLang] || ''}
                  onChange={(e) => setLocField('probable_cause', activeLang, e.target.value)}
                  data-testid={`p-cause-${activeLang}`} />
              </div>

              {/* Steps */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline text-gray-600">{t('patterns.procedure_steps')} ({activeLang})</label>
                  <button type="button" onClick={addStep} className="btn-ghost text-xs text-[#002FA7]" data-testid="p-add-step">
                    <Plus className="w-3.5 h-3.5" /> +
                  </button>
                </div>
                <div className="space-y-2">
                  {form.procedure_steps.map((s, i) => (
                    <div key={s._uid || `step-${i}`} className="bg-gray-50 border border-gray-200 p-3 space-y-2" data-testid={`step-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 bg-[#002FA7] text-white flex items-center justify-center text-xs font-mono font-bold shrink-0">{s.order}</span>
                        <input
                          placeholder={t('patterns.step_action')}
                          className="input-base flex-1" value={s[activeLang] || ''}
                          onChange={(e) => setStepLoc(i, activeLang, e.target.value)}
                          data-testid={`step-action-${i}-${activeLang}`}
                        />
                        <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} className="btn-ghost disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => moveStep(i, +1)} disabled={i === form.procedure_steps.length - 1} className="btn-ghost disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                        {form.procedure_steps.length > 1 && (
                          <button type="button" onClick={() => removeStep(i)} className="btn-ghost text-red-600"><X className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                      <input
                        placeholder={t('patterns.step_expected') + ' (24V, 47.5–48.5V…)'}
                        className="input-base font-mono text-xs" value={s.expected_value || ''}
                        onChange={(e) => setStep(i, 'expected_value', e.target.value)}
                        data-testid={`step-value-${i}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Expected values */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline text-gray-600">{t('patterns.expected_values')} ({activeLang})</label>
                  <button type="button" onClick={addEv} className="btn-ghost text-xs text-[#002FA7]"><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="space-y-2">
                  {form.expected_values.map((e, i) => (
                    <div key={e._uid || `ev-${i}`} className="bg-gray-50 border border-gray-200 p-3 grid grid-cols-12 gap-2" data-testid={`ev-${i}`}>
                      <input placeholder={t('patterns.ev_test')} className="input-base font-mono text-xs col-span-3"
                        value={e.test || ''} onChange={(ev) => setEv(i, 'test', ev.target.value)} />
                      <input placeholder={t('patterns.ev_value')} className="input-base font-mono text-xs col-span-2"
                        value={e.value || ''} onChange={(ev) => setEv(i, 'value', ev.target.value)} />
                      <input placeholder={t('patterns.ev_unit')} className="input-base font-mono text-xs col-span-1"
                        value={e.unit || ''} onChange={(ev) => setEv(i, 'unit', ev.target.value)} />
                      <input placeholder={t('patterns.ev_label') + ` (${activeLang})`} className="input-base text-xs col-span-5"
                        value={e[activeLang] || ''} onChange={(ev) => setEv(i, activeLang, ev.target.value)} />
                      <button type="button" onClick={() => removeEv(i)} className="btn-ghost text-red-600 col-span-1"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline text-gray-600 block mb-1">{t('patterns.notes')} ({activeLang})</label>
                <textarea rows={2} className="input-base" value={form.notes?.[activeLang] || ''}
                  onChange={(e) => setLocField('notes', activeLang, e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-gray-600 block mb-1">{t('patterns.confidence')} (1-5)</label>
                  <input type="number" min="1" max="5" className="input-base font-mono" value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} /></div>
                <div><label className="overline text-gray-600 block mb-1">{t('patterns.success_count')}</label>
                  <input type="number" min="0" className="input-base font-mono" value={form.success_count} onChange={(e) => setForm({ ...form, success_count: e.target.value })} /></div>
              </div>

              {/* Validate translation buttons (only when editing) */}
              {editing && (
                <div className="bg-green-50 border border-green-200 p-3 flex flex-wrap items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-700" />
                  <span className="overline text-green-700">{t('patterns.validate_translation_btn')}:</span>
                  {SUPPORTED_LANGS.map((l) => (
                    <button key={l} type="button" onClick={() => validateTranslation(l)}
                      disabled={form.translation_status?.[l] === 'validated'}
                      data-testid={`validate-${l}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold border border-green-600 text-green-700 hover:bg-green-100 disabled:opacity-30 disabled:cursor-not-allowed">
                      <BadgeCheck className="w-3.5 h-3.5" /> {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">{t('app.cancel')}</button>
              <button type="submit" className="btn-primary" data-testid="p-submit">
                {editing ? t('app.update') : t('app.create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 animate-fade-up" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg">{t('patterns.delete_confirm')}</div>
                <div className="text-sm text-gray-600 mt-1 font-mono">#{confirmDelete.id.slice(0, 8)} — {confirmDelete.brand} {confirmDelete.model}</div>
              </div>
            </div>
            <div className="p-4 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} disabled={deleting} className="btn-secondary">{t('app.cancel')}</button>
              <button onClick={doDelete} disabled={deleting} className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 font-semibold hover:bg-red-700 disabled:opacity-50" data-testid="p-confirm-del">
                <Trash2 className="w-4 h-4" />{t('app.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
